'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect } from 'react';
import BrowserCookies from '~/utilities/BrowserCookies';

// useLayoutEffect fires before the browser paints, so the router.refresh()
// dispatch starts as early as possible — before the user sees stale geo.
// The server-side fallback to useEffect is required because Next.js SSRs
// 'use client' components to HTML; effects never execute on the server either
// way, so this is purely to silence the React warning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type OwnProps = {
  serverZip: string;
};

/**
 * Detects geo staleness caused by the Next.js router serving a cached RSC on
 * back/forward navigation. When the server-rendered zip (baked into the cached
 * RSC payload) no longer matches the current CVCurrentZip cookie, the user has
 * changed their location since this page was last rendered. Calling
 * router.refresh() forces a fresh RSC fetch so geo-dependent content reflects
 * the current location.
 */
export default function GeoStaleChecker({ serverZip }: OwnProps) {
  const router = useRouter();

  useIsomorphicLayoutEffect(() => {
    const clientZip = BrowserCookies.get('CVCurrentZip') ?? '';
    if (clientZip && clientZip !== serverZip) {
      datadogRum.addAction('geo-stale-refresh', { clientZip, serverZip });
      router.refresh();
    }
  }, [router, serverZip]);

  return null;
}
